import { useSupabaseQuery } from './useSupabaseQuery';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useAuth } from './useAuth';
import { BOOKING_STATUS, getBookingStatusLabel, type BookingStatus } from '@/lib/booking-status';
import type { BookingWithDetails } from '@/types';

export const useBookings = () => {
  const { user, isAuthenticated } = useAuth();
  const { supabase } = useSupabaseConnection();

  return useSupabaseQuery<BookingWithDetails[]>(
    ['bookings', user?.id],
    async ({ signal }) => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          trip:trips (
            *,
            product:products (
              *,
              product_courses (
                course:courses (
                  course_id,
                  name,
                  difficulty,
                  start_point,
                  end_point,
                  main_photo_url,
                  estimated_time,
                  length
                )
              )
            )
          ),
          booking_participants (*)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .abortSignal(signal);

      if (error) {
        console.error('Error fetching bookings:', error);
        throw error;
      }

      return data as BookingWithDetails[];
    },
    {
      enabled: isAuthenticated,
    }
  );
};

export const getBookingStatus = (booking: BookingWithDetails) => {
  // UTC 날짜 비교를 위해 ISO string에서 날짜 부분만 사용
  const currentDate = new Date().toISOString().split('T')[0];
  const departureDate = booking.trip.departure_date;
  
  // Invalid date handling - ISO format 검증
  const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(departureDate);
  if (!isValidDate) {
    // Invalid date의 경우 status 기반으로만 판단
    if (booking.status === BOOKING_STATUS.CANCELLED) return 'cancelled';
    if (booking.status === BOOKING_STATUS.CONFIRMED) return 'confirmed';
    if (booking.status === BOOKING_STATUS.PENDING) return 'pending';
    return 'unknown';
  }
  
  // 예약 취소인 경우 (항상 최우선)
  if (booking.status === BOOKING_STATUS.CANCELLED) {
    return 'cancelled';
  }
  
  // Trip 상태가 완료된 경우 (날짜보다 우선)
  if (booking.trip.status === 'completed') {
    return 'completed';
  }
  
  // 출발일이 지났으면 완료된 것으로 간주 (당일은 포함하지 않음)
  if (departureDate < currentDate) {
    return 'completed';
  }
  
  // 상태가 없거나 빈 문자열인 경우
  if (!booking.status || booking.status.trim() === '') {
    return 'unknown';
  }
  
  // 결제가 완료되었으면 확정된 예약
  if (booking.status === BOOKING_STATUS.CONFIRMED) {
    return 'confirmed';
  }
  
  // 결제 대기 중
  if (booking.status === BOOKING_STATUS.PENDING) {
    return 'pending';
  }
  
  // 인식되지 않는 상태
  return 'unknown';
};

// completed와 unknown 상태를 위한 확장 함수
export const getBookingStatusText = (status: string, booking?: any) => {
  // 만료된 pending 예약 확인
  if (status === BOOKING_STATUS.PENDING && booking?.isExpired) {
    return '결제 기한 초과';
  }
  
  // 기본 enum 상태들은 새로운 헬퍼 사용
  if (status === BOOKING_STATUS.PENDING || status === BOOKING_STATUS.CONFIRMED || status === BOOKING_STATUS.CANCELLED) {
    return getBookingStatusLabel(status as BookingStatus);
  }
  
  // 추가 상태들 처리
  switch (status) {
    case 'completed':
      return '여행 완료';
    default:
      return '알 수 없음';
  }
};

export const getBookingStatusVariant = (status: string, booking?: any) => {
  // 만료된 pending 예약은 경고 색상(주황색)
  if (status === BOOKING_STATUS.PENDING && booking?.isExpired) {
    return 'destructive' as const; // 또는 custom variant를 만들어 주황색으로
  }
  
  switch (status) {
    case BOOKING_STATUS.PENDING:
      return 'outline' as const;
    case BOOKING_STATUS.CONFIRMED:
      return 'default' as const;
    case 'completed':
      return 'secondary' as const;
    case BOOKING_STATUS.CANCELLED:
      return 'destructive' as const;
    default:
      return 'secondary' as const;
  }
};